/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { CardActions, CardContent, CardHeader, Grid } from "@mui/material";
import Button from "components/generals/Button";
import Card from "components/generals/Card";
import { Modal, ModalProps as BaseModalProps } from "components/generals/Modal";
import { CSSProperties, FC, useMemo } from "react"

/**
 * Properties for the DialogModal component.
 * Extends base Modal properties with dialog-specific functionality.
 * 
 * @interface ModalProps
 * @extends {BaseModalProps}
 * @property {string} title - Dialog title displayed in header
 * @property {string} closeButtonLabel - Label for close/cancel button
 * @property {Function} onClose - Callback when dialog is closed/cancelled
 * @property {string} [okButtonLabel] - Optional label for OK/confirm button
 * @property {Function} [onOK] - Optional callback when OK/confirm is clicked
 */
interface ModalProps extends BaseModalProps{
    title: string,
    closeButtonLabel: string,
    onClose: ()=>void,
    okButtonLabel?: string,
    onOK?: ()=>void
}

/**
 * CSS style for dialog action buttons.
 * Full-width buttons with centered text and no border radius.
 */
const dialogButtonStyle:CSSProperties = {
    width:"100%",
    textAlign: "center",
    borderRadius: 0
}

/**
 * Dialog modal component with header, content, and action buttons.
 * Provides a consistent dialog UI with optional OK/Cancel or single Close button.
 * 
 * Features:
 * - Card-based modal with header, content, and actions sections
 * - Title in header with bottom border
 * - Flexible content area (children)
 * - Adaptive button layout:
 *   - Single button (close only): aligned right
 *   - Two buttons (close + OK): space-between layout
 * - Responsive grid layout (12 cols mobile, 6 cols each on desktop)
 * - Fixed max width (400px)
 * - Overflow hidden for clean edges
 * 
 * Layout Behavior:
 * - If onOK provided: Shows both Close and OK buttons side-by-side
 * - If onOK omitted: Shows only Close button aligned right
 * 
 * Button Colors:
 * - Close: secondary color
 * - OK: primary color
 * 
 * @param {ModalProps} props - Component properties
 * @param {string} props.title - Dialog title
 * @param {string} props.closeButtonLabel - Close button text
 * @param {Function} props.onClose - Close handler
 * @param {string} [props.okButtonLabel] - OK button text (if onOK provided)
 * @param {Function} [props.onOK] - OK button handler
 * @param {React.ReactNode} props.children - Dialog content
 * 
 * @returns {JSX.Element} Dialog modal with header, content, and action buttons
 */
const DialogModal:FC<ModalProps> = 
    ({title, closeButtonLabel, okButtonLabel, onOK, onClose, ...props}) => {

    const justifyContentActionButtons = useMemo<string>(()=>onOK ? 'space-between':'flex-end' ,[onOK]);

    return <Modal {...props} maxWidth="400px" >
        <Card style={{overflow: 'hidden'}}>
            <CardHeader title={title} style={{borderBottom: '1px solid #dfdfdf', padding: '0.5rem 1.5rem'}}/>
            <CardContent style={{padding: '0.5rem 1.5rem'}}>
                {props.children}
            </CardContent>
            <CardActions sx={{p:0}}>
                <Grid container justifyContent={justifyContentActionButtons}>
                    <Grid item xs={12} md={6}>
                        <Button id="cy-close-button" style={dialogButtonStyle} color="secondary" onClick={()=>onClose()}>{closeButtonLabel}</Button>
                    </Grid>
                    {onOK && <Grid item xs={12} md={6}>
                        <Button id="cy-ok-button" style={dialogButtonStyle} color="primary" onClick={()=>onOK()}>{okButtonLabel}</Button>
                    </Grid>}
                </Grid>
            </CardActions>
        </Card>
    </Modal>
} 

export default DialogModal;